import {
  Badge,
  Card,
  CardHeader,
  CurrencyWrapper,
  Typography,
} from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useFormatInsurancePolicyLimits } from "@/hooks/useFormatInsurancePolicyLimits";
import type { MatterInsurance } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatInsuranceTitle } from "@/lib/utils/format";
import { CircleCheck, CircleHelp } from "lucide-react";

interface SelectedInsuranceCardProps {
  insurance?: MatterInsurance;
  showMedpayBadge?: boolean;
  className?: string;
  isFirstPartyInsurance?: boolean;
}

export const SelectedInsuranceCard = ({
  insurance,
  showMedpayBadge,
  className,
  isFirstPartyInsurance = false,
}: SelectedInsuranceCardProps) => {
  const formatInsurancePolicyLimits = useFormatInsurancePolicyLimits();

  if (!insurance) return null;

  const insuranceTitle = formatInsuranceTitle({
    insurance,
  });

  const showUm =
    isFirstPartyInsurance &&
    (insurance.umExists ||
      insurance.umPerPerson != null ||
      insurance.umPerIncident != null);
  const showUim =
    isFirstPartyInsurance &&
    (insurance.uimExists ||
      insurance.uimPerPerson != null ||
      insurance.uimPerIncident != null);
  const showMedPay =
    isFirstPartyInsurance &&
    (insurance.medPay || insurance.medPayLimit != null);

  return (
    <Card className={cn("border-border/50 w-full", className)}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <Typography
            variant="heading"
            className="text-primary flex flex-wrap items-center gap-2"
          >
            {insuranceTitle?.insurerPart} - {insuranceTitle?.insuredPart} -{" "}
            {insuranceTitle?.insuranceTypePart}
          </Typography>
          {showMedpayBadge && <Badge variant="outline">MedPay</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <Typography variant="subHeading" className="text-muted-foreground">
            Policy: {insurance.policyNumber || "-"} | Claim:{" "}
            {insurance.claimNumber || "-"}
          </Typography>
        </div>
        {!isFirstPartyInsurance && (
          <div className="flex items-center gap-2">
            <Typography
              variant="default"
              className="text-muted-foreground text-xs"
            >
              Policy limits: {formatInsurancePolicyLimits(insurance)}
            </Typography>
            {insurance.policyLimitConfirmation ? (
              <TooltipWrapper message="Policy limits confirmed">
                <CircleCheck className="h-4 w-4 text-green-600" />
              </TooltipWrapper>
            ) : (
              <TooltipWrapper message="Policy limits not confirmed">
                <CircleHelp className="h-4 w-4 text-yellow-600" />
              </TooltipWrapper>
            )}
          </div>
        )}
        {isFirstPartyInsurance && (
          <div className="flex flex-col gap-1">
            {showMedPay && (
              <Typography
                variant="default"
                className="text-muted-foreground flex items-center gap-1 text-xs"
              >
                MedPay:
                <CurrencyWrapper value={insurance.medPayLimit ?? undefined} />
              </Typography>
            )}
            {showUm && (
              <Typography
                variant="default"
                className="text-muted-foreground flex items-center gap-1 text-xs"
              >
                UM:
                <CurrencyWrapper value={insurance.umPerPerson ?? undefined} />
                /
                <CurrencyWrapper value={insurance.umPerIncident ?? undefined} />
              </Typography>
            )}
            {showUim && (
              <Typography
                variant="default"
                className="text-muted-foreground flex items-center gap-1 text-xs"
              >
                UIM:
                <CurrencyWrapper value={insurance.uimPerPerson ?? undefined} />
                /
                <CurrencyWrapper
                  value={insurance.uimPerIncident ?? undefined}
                />
              </Typography>
            )}
          </div>
        )}
      </CardHeader>
    </Card>
  );
};
